import {logout} from './AuthActions'
import {ApiUtil, ForbiddenErrorCode, UnauthorizedErrorCode, UnknownNetworkErrorCode} from '../api/ApiUtil'
import {BackendAPI} from '../api/BackendAPI'
import type {ClaimsResponse, FailedResponse} from '../api/types'
import type {AppDispatch, GetStateFunc} from '../store'
import {ActionUtil} from "./ActionUtil";
import {useAuthorizationVerified} from "./StateActions";

/**
 * User related action types.
 */
export enum UserActionType {
  CLAIMS_PENDING = 'CLAIMS_PENDING',
  CLAIMS_FULFILLED = 'CLAIMS_FULFILLED',
  CLAIMS_REJECTED = 'CLAIMS_REJECTED',
  CLAIMS_CLEAR = 'CLAIMS_CLEAR',
}

interface FetchUserClaimsAction {
  type: UserActionType.CLAIMS_PENDING
}

interface FetchUserClaimsSuccessAction {
  type: UserActionType.CLAIMS_FULFILLED
  payload: {
    email: string
    isAuthorized: boolean
    isAdmin: boolean
  }
}

interface FetchUserClaimsFailureAction {
  type: UserActionType.CLAIMS_REJECTED
  payload: {
    error: string
  }
}

interface ClearClaimsAction {
  type: UserActionType.CLAIMS_CLEAR
}

export type UserAction =
  | ClearClaimsAction
  | FetchUserClaimsAction
  | FetchUserClaimsSuccessAction
  | FetchUserClaimsFailureAction

export function fetchClaims(): (dispatch: AppDispatch, getState: GetStateFunc) => Promise<void> {
  return async (dispatch: AppDispatch, getState: GetStateFunc): Promise<void> => {
    const token = ActionUtil.getToken(getState, 'fetchClaims')
    if (!token) {
      return
    }

    dispatch<UserAction>({type: UserActionType.CLAIMS_PENDING})

    const claimsRes = await BackendAPI.getClaims(token)
    if (claimsRes.succeeded) {
      dispatch<UserAction>(claimsUpdate(claimsRes.payload))
      if (claimsRes.payload.isAuthorized) {
        // Trigger data fetches after authorization has been cleared
        await dispatch(useAuthorizationVerified())
      }
      return
    }

    if (shouldForceLogout(claimsRes)) {
      // Force logout
      dispatch(logout())
    }
    // Do not notify about un-authorization error as the error message's reason would need guessing here
    if (claimsRes.errStatusCode !== UnauthorizedErrorCode) {
      const errMsg = ApiUtil.resolveLocalizedErrorMessage(claimsRes)
      dispatch<UserAction>(getClaimsError(errMsg))
    }
  }
}

export function clearClaims(): UserAction {
  return {
    type: UserActionType.CLAIMS_CLEAR
  }
}

/* Helper Functions */

function shouldForceLogout(res: FailedResponse): boolean {
  switch (res.errStatusCode) {
    case UnauthorizedErrorCode:
    case ForbiddenErrorCode:
    case UnknownNetworkErrorCode:
      return true
    default:
      return false
  }
}

function getClaimsError(error: string): UserAction {
  return {
    type: UserActionType.CLAIMS_REJECTED,
    payload: {error}
  }
}

function claimsUpdate(claims: ClaimsResponse): UserAction {
  return {
    type: UserActionType.CLAIMS_FULFILLED,
    payload: {
      email: claims.email ?? '',
      isAuthorized: claims.isAuthorized,
      isAdmin: claims.isAdmin
    }
  }
}
